/**
 * d3 implementation of the inverted (horizontal) grouped bar chart.
 *
 * Drop-in for `mountHorizontalBarChart` in `../horizontalBarChartCore.ts` — same
 * signature, same return contract — so swapping engines needs no adapter layer.
 *
 * Only `d3-scale` is used. Element creation goes through plain DOM calls rather than
 * `d3-selection`, which keeps the bundle small and the output easy to reason about
 * when diffing against Highcharts' SVG.
 */

import { scaleLinear } from "d3-scale";
import type { HorizontalBarChartPayload } from "../../normalize.js";
import {
  horizontalBarChartHeight,
  horizontalBarValueTicks,
  seriesColors,
} from "../horizontalBarChartCore.js";
import { horizontalBarPlotWidth } from "../geometry/plotWidth.js";
import { renderResponsive, runAnimation } from "./dom.js";

/**
 * How far right of the label-box edge the plot begins. Started at a measured 15.2
 * and nudged to 17.2 on review — the left edge read a couple of pixels too far left.
 */
const CATEGORY_LABEL_DRAW_OFFSET_PX = 17.2;

const SVG_NS = "http://www.w3.org/2000/svg";

/** Matches the Highcharts config in horizontalBarChartCore.ts. */
const SPACING_LEFT = 20;
const SPACING_RIGHT = 32;
const SPACING_TOP = 10;
/** Measured from the baseline: the plot spans y=10..374 in a 416px chart. */
const SPACING_BOTTOM = 42;
const GROUP_PADDING = 0.14;
const MAX_POINT_WIDTH = 20;
const AXIS_COLOR = "#ccc";
const LABEL_COLOR = "#1a1a1a";
const LABEL_FONT_SIZE = 11;
/**
 * Vertical nudge that optically centers a category label in its band. Text `y` is the
 * baseline, not the box top, so a small positive offset is needed.
 */
const CATEGORY_LABEL_BASELINE_NUDGE = 5;

/**
 * Clearance between the value-axis rule and the tick numbers below it.
 *
 * Set by review, not by measuring Highcharts. Because `y` on a text element is the
 * baseline, the visible gap is roughly this minus the font ascent (~8px at 11px), so
 * 14 reads as about 6px of white space.
 */
const VALUE_LABEL_GAP_PX = 14;

function el<K extends keyof SVGElementTagNameMap>(
  name: K,
  attrs: Record<string, string | number> = {}
): SVGElementTagNameMap[K] {
  const node = document.createElementNS(SVG_NS, name);
  for (const [k, v] of Object.entries(attrs)) node.setAttribute(k, String(v));
  return node;
}

function labelStyle(node: SVGElement): void {
  node.setAttribute("font-family", "var(--an26-font-mono), monospace");
  node.setAttribute("font-weight", "300");
  node.setAttribute("font-size", `${LABEL_FONT_SIZE}px`);
  node.setAttribute("fill", LABEL_COLOR);
}

/** Series order: portfolio first, benchmark last — mirrors the Highcharts sort. */
function sortOrder(name: string): number {
  if (/benchmark/i.test(name)) return 1;
  if (/portfolio/i.test(name)) return 0;
  return 2;
}

export interface MountD3HorizontalBarOptions {
  esc: (s: string) => string;
}

function drawHorizontalBar(
  mount: HTMLElement,
  data: HorizontalBarChartPayload,
  opts: MountD3HorizontalBarOptions,
  animateIn: boolean
): () => void {
  const { esc } = opts;
  const chartWidth = mount.clientWidth;
  const chartHeight = horizontalBarChartHeight(data.categories.length);

  const ticks = horizontalBarValueTicks(data, chartWidth);
  const { plotLeft } = horizontalBarPlotWidth({
    chartWidth,
    categories: data.categories,
    spacingLeft: SPACING_LEFT,
  });

  // The predictor reports the label-box edge, which is what tick selection needs.
  // Highcharts starts its bars 15.2px further right — measured as a constant offset
  // across every value tick. Applied here, not in the shared predictor, because
  // widening that changes tick selection and breaks the Highcharts gate.
  const drawLeft = plotLeft + CATEGORY_LABEL_DRAW_OFFSET_PX;
  // For *drawing*, the right inset is plain spacingRight — the 45.2px figure in the
  // predictor includes room for the final value label and is only for tick selection.
  const plotWidth = Math.max(0, chartWidth - drawLeft - SPACING_RIGHT);
  const plotTop = SPACING_TOP;
  const plotHeight = Math.max(0, chartHeight - plotTop - SPACING_BOTTOM);

  const x = scaleLinear().domain([ticks.min, ticks.max]).range([drawLeft, drawLeft + plotWidth]);

  const svg = el("svg", {
    width: chartWidth,
    height: chartHeight,
    viewBox: `0 0 ${chartWidth} ${chartHeight}`,
  });
  svg.style.display = "block";

  const ordered = [...data.series].sort((a, b) => sortOrder(a.name) - sortOrder(b.name));

  const valueAxisY = plotTop + plotHeight;
  const bandHeight = data.categories.length ? plotHeight / data.categories.length : plotHeight;

  /* ---- value axis: labels below the plot, plus its baseline rule ---- */
  svg.append(
    el("line", {
      x1: drawLeft,
      y1: valueAxisY,
      x2: drawLeft + plotWidth,
      y2: valueAxisY,
      stroke: AXIS_COLOR,
      "stroke-width": 1,
    })
  );
  ticks.positions.forEach((tick, ti) => {
    // The last label right-aligns to the axis end rather than centering on it, so it
    // sits inside the chart edge instead of straddling it.
    const isLast = ti === ticks.positions.length - 1;
    const label = el("text", {
      x: x(tick),
      y: valueAxisY + LABEL_FONT_SIZE + VALUE_LABEL_GAP_PX,
      "text-anchor": isLast ? "end" : "middle",
    });
    labelStyle(label);
    label.textContent = String(tick);
    svg.append(label);
  });

  /* ---- category axis: right-aligned labels, plus its rule ---- */
  svg.append(
    el("line", {
      x1: drawLeft,
      y1: plotTop,
      x2: drawLeft,
      y2: valueAxisY,
      stroke: AXIS_COLOR,
      "stroke-width": 1,
    })
  );

  data.categories.forEach((category, ci) => {
    const label = el("text", {
      x: plotLeft,
      y: plotTop + bandHeight * (ci + 0.5) + CATEGORY_LABEL_BASELINE_NUDGE,
      "text-anchor": "end",
    });
    labelStyle(label);
    label.textContent = category;
    svg.append(label);
  });

  /* ---- bars ----
   * Highcharts' grouping model, reproduced: the band shrinks by groupPadding on
   * both sides, the group splits evenly between series, each bar is capped at
   * maxPointWidth, and the result is centered in the band. pointPadding is 0
   * today, so bars within a group touch.
   */
  const seriesCount = ordered.length || 1;
  const groupHeight = bandHeight * (1 - 2 * GROUP_PADDING);
  const barHeight = Math.min(groupHeight / seriesCount, MAX_POINT_WIDTH);

  const barsBySeries: SVGRectElement[][] = ordered.map(() => []);
  /** Final width per bar, for the entrance animation. */
  const grown: Array<{ rect: SVGRectElement; width: number }> = [];

  ordered.forEach((series, si) => {
    const { baseColor } = seriesColors(series.name, si);
    data.categories.forEach((_, ci) => {
      const value = series.data[ci];
      if (value == null || !Number.isFinite(value)) return;

      const bandTop = plotTop + bandHeight * ci;
      const groupTop = bandTop + (bandHeight - barHeight * seriesCount) / 2;
      const width = Math.max(0, x(value) - x(ticks.min));

      const rect = el("rect", {
        x: x(Math.min(0, value)),
        y: groupTop + barHeight * si,
        width,
        height: barHeight,
        fill: baseColor,
      });
      rect.dataset.series = String(si);
      rect.dataset.value = String(value);
      rect.dataset.name = series.name;
      barsBySeries[si]!.push(rect);
      grown.push({ rect, width });
      svg.append(rect);
    });
  });

  mount.replaceChildren(svg);

  // Bars grow rightward out of the category axis.
  const stopAnimation = animateIn
    ? runAnimation((t) => {
        for (const { rect, width } of grown) rect.setAttribute("width", String(width * t));
      })
    : null;

  /* ---- cross-series fade + tooltip ---- */
  const tooltip = document.createElement("div");
  tooltip.style.position = "absolute";
  tooltip.style.pointerEvents = "none";
  tooltip.style.display = "none";
  tooltip.style.zIndex = "8";
  mount.style.position = mount.style.position || "relative";
  mount.append(tooltip);

  const setFade = (activeIndex: number | null): void => {
    ordered.forEach((series, si) => {
      const { baseColor, fadedColor } = seriesColors(series.name, si);
      const color = activeIndex !== null && si !== activeIndex ? fadedColor : baseColor;
      for (const rect of barsBySeries[si]!) rect.setAttribute("fill", color);
    });
  };

  const onOver = (event: Event): void => {
    const rect = event.target as SVGRectElement;
    if (!rect.dataset?.series) return;
    const si = Number(rect.dataset.series);
    setFade(si);

    const value = Number(rect.dataset.value);
    tooltip.innerHTML = `<div class="an26-tooltip-hc"><div class="an26-tooltip-hc__line">${esc(
      String(rect.dataset.name)
    )}: <span class="an26-tooltip-hc__val">${esc(
      Number.isFinite(value) ? value.toFixed(2) : "—"
    )}</span></div></div>`;
    tooltip.style.display = "block";
    tooltip.style.left = `${Number(rect.getAttribute("x")) + Number(rect.getAttribute("width")) + 8}px`;
    tooltip.style.top = `${Number(rect.getAttribute("y")) - 4}px`;
  };

  const onLeave = (): void => {
    setFade(null);
    tooltip.style.display = "none";
  };

  svg.addEventListener("mouseover", onOver);
  mount.addEventListener("mouseleave", onLeave);

  return () => {
    stopAnimation?.();
    svg.removeEventListener("mouseover", onOver);
    mount.removeEventListener("mouseleave", onLeave);
    mount.replaceChildren();
  };
}

/**
 * Public entry point: draws immediately and redraws on container resize, so the
 * chart stays correct when a device rotates or a layout reflows.
 */
export function mountD3HorizontalBarChart(
  mount: HTMLElement,
  data: HorizontalBarChartPayload,
  opts: MountD3HorizontalBarOptions
): () => void {
  return renderResponsive(mount, (animateIn) => drawHorizontalBar(mount, data, opts, animateIn));
}
